<script>
import * as form_enumeration_utils from '@/util/form_enumeration_util'
import FQDNSelection from '@/components/requests/vmware/FQDNSelection.vue'
import RuleFormGroup from '@/components/requests/vmware/RuleFormGroup.vue'
import {
  costCenterRules,
  cpuRules,
  diskRules,
  intendedUseRules,
  ramRules, sshKeysRules
} from '@/components/requests/vmware/vmware-vm.rules'
import TemplateSelection from '@/components/requests/vmware/TemplateSelection.vue'
import {emptyContact, LIMITS, SCC_REGEX} from '@/components/requests/vmware/vmware-vm.config'
import ContactFormInput from '@/components/requests/vmware/ContactFormInput.vue'
import AccountsInput from '@/components/requests/vmware/AccountsInput.vue'
import requestsService from '@/api-services/requests.service'
import OuInput from '@/components/requests/vmware/OuInput.vue'

const initialForm = () => ({
  fqdn: null,
  ou: null,
  intended_use: '',
  template: null,
  other_os: '',
  cpu_cores: null,
  ram_gb: null,
  disk_gb: null,
  technical_contact: emptyContact(),
  administrative_contact: emptyContact(),
  budget_contact: emptyContact(),
  cost_center: '',
  ssh_keys: '',
  accounts: [''],
  comment: '',
})

export default {
  name: 'VMwareVMRequestform',
  components: { OuInput, AccountsInput, ContactFormInput, TemplateSelection, FQDNSelection, RuleFormGroup },
  data() {
    return {
      error_message: null,
      sending: false,
      status_icon: null,

      // Form values
      form: initialForm(),
      validity: {
        fqdn: false,
        template: false,
        cpu_cores: false,
        ram_gb: false,
        disk_gb: false
      },
      LIMITS
    }
  },
  watch: {
  },
  methods: {
    setValidity(key, v) {
      this.$set(this.validity, key, v)
    },
    reset() {
      this.form = initialForm()
      this.error_message = null
      this.status_icon = null
      // validity nicht leeren: die Kinder melden ihren Zustand selbst neu
    },
    build_vm_request_data() {
      const f = this.form
      const not_scc = this.is_selected_ou_not_scc
      const has_budget_contact = not_scc && (f.budget_contact.name.length > 0 || f.budget_contact.email.length > 0)
      const trim = s => (s ?? '').trim()

      return {
        fqdn: f.fqdn,
        assigned_oe: {
          gpk: f.ou.gpk,
          name: f.ou.fq_name
        },
        intended_use: f.intended_use,
        template: {
          template_name: f.template.name,
          os_type: f.template.os,
        },
        other_os_name: f.template.os === 'Other' ? f.other_os : '',
        cpu_cores: f.cpu_cores,
        ram_gb: f.ram_gb,
        disk_gb: f.disk_gb,
        technical_contact: f.technical_contact,
        administrative_contact: f.administrative_contact,
        budget_contact: not_scc && has_budget_contact ? f.budget_contact : null,
        cost_center: not_scc ? f.cost_center : null,
        ssh_keys: this.is_linux_template ? trim(f.ssh_keys) : null,
        accounts: f.accounts.map(trim).filter(a => a.length > 0),
        comment: trim(f.comment)
      }
    },
    send_vm_request(request_data) {
      this.sending = true
      requestsService.send_vm_request(this.$store.state, request_data).then(() => {
        this.sending = false
        this.status_icon = 'request_sent'
        setTimeout(() => {
          this.$bvModal.hide('vmware-vm-request-form')
        }, 1000)
      }).catch(err => {
        window.console.error(err)
        this.sending = false
        this.status_icon = 'transaction_error'
        if (err.response.status >= 400 && err.response.status < 500) {
          this.error_message = err.response.data.detail
        } else {
          this.error_message = `${this.$t('system.internal_error')}: ${err.response.status}`
        }
      })
    },
    on_submit_form(e) {
      e.preventDefault()
      const requestData = this.build_vm_request_data()
      this.send_vm_request(requestData)
    },
    label(field, i18nKey, params = {}) {
      return this.$t(`global.enumeration.${this.form_field_enumeration_indices[field]}`, {
        msg: this.$t(`components.requests.vmware.${i18nKey}`, params),
      })
    },
  },
  computed: {
    form_field_enumeration_indices() {
      const fields = {}
      form_enumeration_utils.push_form_field(fields, 'fqdn')
      form_enumeration_utils.push_form_field(fields, 'ou')
      form_enumeration_utils.push_form_field(fields, 'intended_use')
      form_enumeration_utils.push_form_field(fields, 'template')
      form_enumeration_utils.push_form_field(fields, 'cpu_cores')
      form_enumeration_utils.push_form_field(fields, 'ram_gb')
      form_enumeration_utils.push_form_field(fields, 'disk_gb')
      form_enumeration_utils.push_form_field(fields, 'technical_contact')
      form_enumeration_utils.push_form_field(fields, 'administrative_contact')

      if (this.is_selected_ou_not_scc) {
        form_enumeration_utils.push_form_field(fields, 'budget_contact')
        form_enumeration_utils.push_form_field(fields, 'cost_center')
      }

      if (this.is_linux_template) {
        form_enumeration_utils.push_form_field(fields, 'ssh_keys')
      }

      form_enumeration_utils.push_form_field(fields, 'accounts')
      form_enumeration_utils.push_form_field(fields, 'comments')

      return fields
    },
    intended_use_rules() {
      return intendedUseRules(this.form.intended_use, this.$t.bind(this))
    },
    required_fields() {
      const keys = ['fqdn', 'intended_use', 'template', 'cpu_cores', 'ram_gb', 'disk_gb',
        'technical_contact', 'administrative_contact', 'accounts']
      if (this.is_selected_ou_not_scc) keys.push('budget_contact', 'cost_center')
      if (this.is_linux_template) keys.push('ssh_keys')
      return keys
    },
    isFormValid() {
      return this.required_fields.every(k => this.validity[k] === true)
    },
    os() { return this.form.template?.os ?? null },
    is_linux_template() { return this.os === 'Linux' },
    cpu_rules() { return cpuRules(this.form.cpu_cores, this.os, this.$t.bind(this)) },
    ram_rules() { return ramRules(this.form.ram_gb, this.os, this.$t.bind(this)) },
    disk_rules() { return diskRules(this.form.disk_gb, this.os, this.$t.bind(this)) },
    min_cpu() { return LIMITS.cpu.minByOs[this.os] },
    min_ram() { return LIMITS.ram.minByOs[this.os] },
    min_disk() { return LIMITS.disk.minByOs[this.os] },
    is_selected_ou_not_scc() {
      if (!this.form.ou) return false
      return !SCC_REGEX.test(this.form.ou.fq_name)
    },
    cost_center_rules() {
      return costCenterRules(this.form.cost_center, this.$t.bind(this))
    },
    ssh_keys_rules() {
      return sshKeysRules(this.form.ssh_keys, this.$t.bind(this))
    },
  }
}
</script>

<template>
  <b-modal id="vmware-vm-request-form" size="xl" @ok="on_submit_form">
    <template #modal-title>
      Antrag auf eine VM auf Basis VMware vSphere
    </template>
    <b-alert :show="error_message !== null" variant="danger" dismissible>
      {{ error_message }}
    </b-alert>
    <b-overlay rounded="sm">
      <FQDNSelection :field_enumeration_index="form_field_enumeration_indices['fqdn']" v-model="form.fqdn" @input="form.fqdn=$event"  @validity-change="setValidity('fqdn', $event)"/>
      <OuInput :label="label('ou', 'associated_ous')" v-model="form.ou" @input="form.ou=$event" @validity-change="setValidity('ou', $event)"/> />
      <!-- Intended Use of the VM -->
      <RuleFormGroup v-model="form.intended_use" optional
                     :label="$t(`global.enumeration.${form_field_enumeration_indices['intended_use']}`, { msg: $t('components.requests.vmware.intended_use.label') })"
                     :rules="intended_use_rules"
                     @validity-change="setValidity('intended_use', $event)">
        <template #default="{ state }">
          <b-form-textarea v-model="form.intended_use" :state="state"
                           :placeholder="$t('components.requests.vmware.intended_use.placeholder')"
                           rows="3" max-rows="6" />
        </template>
      </RuleFormGroup>
      <TemplateSelection v-model="form.template"
                         :other-os.sync="form.other_os"
                         :field_enumeration_index="form_field_enumeration_indices['template']"
                         @validity-change="setValidity('template', $event)" />
      <!-- Hardwre requirements -->
      <template v-if="form.template">
        <RuleFormGroup v-model="form.cpu_cores" type="number"
                       :label="label('cpu_cores', 'cpu_cores.label')"
                       :description="$t('components.requests.vmware.cpu_cores.description', { min: min_cpu, max: LIMITS.cpu.max })"
                       :rules="cpu_rules"
                       @validity-change="setValidity('cpu_cores', $event)" />

        <RuleFormGroup v-model="form.ram_gb" type="number"
                       :label="label('ram_gb', 'ram_gb.label')"
                       :description="$t('components.requests.vmware.ram_gb.description', { min: min_ram, max: LIMITS.ram.max })"
                       :rules="ram_rules"
                       @validity-change="setValidity('ram_gb', $event)" />

        <RuleFormGroup v-model="form.disk_gb" type="number"
                       :label="label('disk_gb', 'disk_gb.label')"
                       :description="$t('components.requests.vmware.disk_gb.description', { min: min_disk })"
                       :rules="disk_rules"
                       @validity-change="setValidity('disk_gb', $event)" />

        <b-form-group class="mb-0" :label="label('technical_contact', 'technical_contact_person.label')">
          <ContactFormInput v-model="form.technical_contact"
                            @validity-change="setValidity('technical_contact', $event)" />
        </b-form-group>

        <b-form-group :label="label('administrative_contact', 'administrative_contact_person.label')">
          <ContactFormInput v-model="form.administrative_contact"
                            @validity-change="setValidity('administrative_contact', $event)" />
        </b-form-group>

        <template v-if="is_selected_ou_not_scc">
          <b-form-group :label="label('budget_contact', 'budget_contact_person.label')">
            <ContactFormInput v-model="form.budget_contact"
                              @validity-change="setValidity('budget_contact', $event)" />
          </b-form-group>
          <RuleFormGroup v-model="form.cost_center"
                         :label="label('cost_center', 'cost_center.label')"
                         :rules="cost_center_rules"
                         @validity-change="setValidity('cost_center', $event)" />
        </template>
        <RuleFormGroup v-if="is_linux_template" v-model="form.ssh_keys"
                       :label="label('ssh_keys', 'ssh_keys.label')"
                       :rules="ssh_keys_rules"
                       @validity-change="setValidity('ssh_keys', $event)">
          <template #description>
            <i18n path="components.requests.vmware.ssh_keys.description">
              <template #link>
                <a href="https://docs.cert.kit.edu/ssh/en/" target="_blank" rel="noopener noreferrer">SSH at KIT</a>
              </template>
            </i18n>
          </template>
          <template #default="{ state }">
            <b-form-textarea v-model="form.ssh_keys" :state="state"
                             :placeholder="$t('components.requests.vmware.ssh_keys.placeholder')" />
          </template>
        </RuleFormGroup>
        <AccountsInput v-model="form.accounts"
                       :label="label('accounts', 'accounts.label', { ou: 'tst' })"
                       @validity-change="setValidity('accounts', $event)" />
      </template>
      <b-form-group :label="label('comments', 'additional_comments')">
        <b-form-textarea v-model="form.comment"
                         :placeholder="$t('components.requests.vmware.comments.placeholder')"
                         rows="3" max-rows="8" />
      </b-form-group>
    </b-overlay>
    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="reset">{{ $t('components.requests.all.reset_request') }}</b-button>
      <b-button variant="secondary" @click="cancel()">{{ $t('components.requests.all.cancel') }}</b-button>
      <b-button variant="primary" :disabled="!isFormValid" @click="ok()">
        {{ $t('components.requests.all.send') }}
        <b-spinner small v-show="sending" />
        <netvs-icon v-show="status_icon !== null" :icon="status_icon" />
      </b-button>
    </template>
  </b-modal>
</template>
